<template>
  <BaseModal
    :heading="heading"
    small
    useForm
    @submit="submit"
    @close="emit('close')"
  >
    <Flex direction="column">
      <BaseText>{{ info }}</BaseText>
      <BaseInput
        v-model="email"
        :label="t('global.email_address')"
        type="email"
        required
        v-test="'email-receipt-email'"
      />
    </Flex>
    <template #footer>
      <BaseButton
        @click="emit('close')"
        color="inverted"
        v-test="'email-receipt-cancel'"
      >
        {{ t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        submitForm
        :loading="sendingEmail || sendingSaleDocument"
        v-test="'email-receipt-send'"
      >
        {{ t('global.actions.send') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { flash } from '@/helpers/ui';
import { useUserStore } from '@/stores/user';
import { MessageTypeEnum } from '@/types';

const { t } = useI18n();

const { hasFeatureFlag } = useUserStore();

const props = withDefaults(
  defineProps<{
    orderId?: number;
    documentId?: string;
    email?: string;
    type?: 'receipt' | 'invoice';
  }>(),
  {
    type: 'receipt',
  },
);

const emit = defineEmits(['submit', 'close']);

const heading = computed(() =>
  props.type === 'invoice'
    ? t('register.send_digital_invoice')
    : t('register.send_digital_receipt'),
);
const info = computed(() =>
  props.type === 'invoice'
    ? t('register.digital_invoice_email_info')
    : t('register.digital_receipt_email_info'),
);

const { mutate: sendEmail, loading: sendingEmail } = useMutation(gql`
  mutation createMessage($input: CreateMessageInput!) {
    createMessage(input: $input) {
      errors
    }
  }
`);

const { mutate: sendSaleDocument, loading: sendingSaleDocument } =
  useMutation(gql`
  mutation sendSaleDocument($input: SendSaleDocumentInput!) {
    sendSaleDocument(input: $input) {
      errors
    }
  }
`);

const email = ref(props.email || '');

const submit = () => {
  sendMutation().then(({ data }: any) => {
    const errors = hasFeatureFlag('compliant-register')
      ? data.sendSaleDocument.errors
      : data.createMessage.errors;

    if (!errors) {
      flash(t('global.flash.email_sent'));
      emit('submit');
    }
  });
};

const sendMutation = () => {
  if (hasFeatureFlag('compliant-register')) {
    return sendSaleDocument({
      input: {
        documentId: props.documentId,
        email: email.value,
      },
    });
  } else {
    return sendEmail({
      input: {
        destination: email.value,
        messageType: MessageTypeEnum.OrderInvoice,
        orderId: props.orderId,
      },
    });
  }
};
</script>
